import './filters.less'
import ToggleFilter from './components/toggleFilter'
import FilterDiscreet from './components/filterDiscreet'
import SelectedFiltersControl from './components/selectedFiltersControl'
import FilterGrouped from './components/filterGrouped'
import { managePageFilters, removeAllPageFilters, UrlContextParams } from '../../utils/urlUtil'
import {
  SORT_BY,
  FILTER_TYPES,
  END_DATE,
  IS_ALL,
  IS_AT_RISK,
  IS_DRAFT,
  IS_RESOLVED,
  IS_SENSITIVE,
  IS_UNRESOLVED,
  PAGE,
  SORT_DIRECTION,
  START_DATE,
  PAGE_VIEW_KEY,
  SEARCH_QUERY,
  PAGE_SIZE,
  FILTER_TYPES_V2
} from '../../constants'
import React, { SyntheticEvent, useState } from 'react'
import { Dropdown, Popup } from 'semantic-ui-react'
import { Button as LbButton } from '@lightbeamai/design-system'
import cx from 'classnames'
import { Funnel as IconFunnel } from 'phosphor-react'
import { FormattedMessage, useIntl } from 'react-intl'

export type FilterValueGroup = { groupId: string; groupName: string; groupDisabled?: boolean }
export type FilterValuesData = {
  key: string
  name: string
  isDisabled?: boolean
  groups?: FilterValueGroup[]
}

export interface FilterPaginationProps {
  totalCount: number
  currentCount: number
  pageSize: number
  isLoading?: boolean
  onLoadMore: () => void
}

export interface SearchConfig {
  onSearch: (query: string) => void
  minimumKeywordLength?: number
}

export interface FilterItemProps {
  title: string
  key: string
  keyChild?: string
  titleGroup?: string
  tooltip?: string
  disabledTooltip?: string
  values: string[]
  valuesData?: FilterValuesData[]
  type?: FILTER_TYPES | FILTER_TYPES_V2
  allowParentSelection?: boolean
  isSorted?: boolean
  filterPaginationProps?: FilterPaginationProps
  searchConfig?: SearchConfig
}
export interface FilterIProps {
  context: UrlContextParams
  pageName: string
  title?: string
  compact?: boolean
  filters: FilterItemProps[]
  sort?: { value?: string; text: string; selected?: boolean }[]
  changeFunction?: (key: string, setValue?: string | string[]) => void
}

const TabFilters = [
  PAGE,
  PAGE_SIZE,
  SORT_DIRECTION,
  SORT_BY,
  START_DATE,
  END_DATE,
  `${IS_SENSITIVE}_${PAGE}`, // Todo; this type of page filter name should be generalized
  `${IS_AT_RISK}_${PAGE}`,
  `${IS_UNRESOLVED}_${PAGE}`,
  `${IS_RESOLVED}_${PAGE}`,
  `${IS_SENSITIVE}_${PAGE_SIZE}`, // Todo; this type of page filter name should be generalized
  `${IS_AT_RISK}_${PAGE_SIZE}`,
  `${IS_UNRESOLVED}_${PAGE_SIZE}`,
  `${IS_RESOLVED}_${PAGE_SIZE}`
]
const PageFilters = [
  IS_ALL,
  IS_AT_RISK,
  IS_SENSITIVE,
  IS_DRAFT,
  IS_RESOLVED,
  IS_UNRESOLVED,
  PAGE_VIEW_KEY,
  SEARCH_QUERY
]

const FiltersComponent = (props: FilterIProps): React.ReactElement => {
  const {
    filters,
    context,
    pageName: filterPage,
    changeFunction,
    compact = false,
    sort = []
  } = props
  const intl = useIntl()
  const [isFiltersOpened, setIsFiltersOpened] = useState(false)

  let allFilterNames: FilterValuesData[] = []
  filters.forEach((filter) => {
    if (filter.valuesData) {
      allFilterNames = [...allFilterNames, ...filter.valuesData]
    }
  })

  const handleClose = () => setIsFiltersOpened(false)
  const handleOpen = () => setIsFiltersOpened(true)
  const handleClearFilters = () => removeAllPageFilters(filterPage)
  const filtersToControl = Object.entries(context.filters)
    .filter(([key]) => ![...TabFilters, ...PageFilters].includes(key))
    .map(([key, value]) => {
      return Array.isArray(value) ? value.map((val) => ({ key, value: val })) : { key, value }
    })
    .flat()
  const setSort = (_: SyntheticEvent, data) => {
    setSelectedSortValue(data.value)
    managePageFilters({
      pageName: filterPage,
      filterKey: SORT_BY,
      filterValues: [data.value],
      action: data.value ? 'add' : 'remove'
    })
  }

  const defaultSortValue = sort.find((selected) => !!selected)?.value
  const [selectedSortValue, setSelectedSortValue] = useState(context.pageSort || defaultSortValue)

  return (
    <>
      {sort.length ? (
        <div className="sort-container">
          <div className="sort-label">
            <label htmlFor="sort">
              <FormattedMessage id="label.text.sort" />
            </label>
          </div>
          <div className="xs-mr-12 xs-ml-12 sort-select">
            <Dropdown
              placeholder={intl.formatMessage({
                id: 'sort.dropdown.placeholder'
              })}
              clearable
              selection
              defaultValue={selectedSortValue}
              options={sort}
              onChange={setSort}
            />
          </div>
        </div>
      ) : (
        ''
      )}
      <div className={cx('filters-wrapper', { compact })} data-test-id="filters-filters-component">
        <Popup
          className="filters-popup"
          on="click"
          open={isFiltersOpened}
          position="bottom center"
          onClose={handleClose}
          onOpen={handleOpen}
          trigger={
            <div className="filters-control">
              <LbButton
                variant={isFiltersOpened ? 'primary' : 'secondary'}
                className={`filter-trigger`}
                size={40}
              >
                <IconFunnel
                  color={isFiltersOpened ? 'var(--color-white)' : '#959DA5'}
                  size={20}
                  weight="fill"
                />
              </LbButton>
              <div className="filter-divider"></div>

              {!compact && Object.keys(filtersToControl).length === 0 && (
                <LbButton
                  variant={isFiltersOpened ? 'primary' : 'secondary'}
                  className={`filter-trigger name`}
                  size={40}
                >
                  {intl.formatMessage({ id: 'text.filter' })}
                </LbButton>
              )}
            </div>
          }
          content={
            <div className="filters-container custom-scrollbar">
              {filters.map((filter, index) => {
                const filterType = filter.type || FILTER_TYPES.discreet

                return (
                  <React.Fragment key={index}>
                    {filterType === FILTER_TYPES.toggle && (
                      <ToggleFilter
                        filterPage={filterPage}
                        filters={context.filters}
                        filterOptions={filter.values}
                        filterOptionsData={filter.valuesData}
                        filterKey={filter.key}
                        filterTitleI18NId={filter.title}
                        isSorted={filter.isSorted}
                        changeFunction={changeFunction}
                        closeFilters={handleClose}
                      />
                    )}
                    {filterType === FILTER_TYPES.discreet && (
                      <FilterDiscreet
                        filterPage={filterPage}
                        filters={context.filters}
                        filterOptions={filter.values}
                        filterOptionsData={filter.valuesData}
                        filterKey={filter.key}
                        filterTitleI18NId={filter.title}
                        disabledTooltip={filter.disabledTooltip}
                        changeFunction={changeFunction}
                        closeFilters={handleClose}
                      />
                    )}
                    {filterType === FILTER_TYPES.grouped && !!filter.valuesData?.length && (
                      <FilterGrouped
                        titleGroup={filter.titleGroup}
                        filterPage={filterPage}
                        filters={context.filters}
                        filterOptionsData={filter.valuesData}
                        filterKeyParent={filter.key}
                        filterKeyChild={filter.keyChild || ''}
                        filterTitleI18NId={filter.title}
                        tooltip={filter.tooltip}
                        disabledTooltip={filter.disabledTooltip}
                        changeFunction={changeFunction}
                        allowParentSelection={filter.allowParentSelection}
                        closeFilters={handleClose}
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </div>
          }
        />
        {!compact && filtersToControl.length > 0 && (
          <>
            <SelectedFiltersControl
              filters={filtersToControl}
              filterValuesData={allFilterNames}
              pageName={filterPage}
            />
            <LbButton
              className="clear-filters"
              variant="link"
              size={24}
              onClick={handleClearFilters}
            >
              {<FormattedMessage id="filters.button.clearAll" />}
            </LbButton>
          </>
        )}
      </div>
    </>
  )
}

export default FiltersComponent
